// Artlines custom design hub on the Custom Designs page (snippets/artlines-custom-hub.liquid).
// Layout (decision 2026-10-16, docs/decisions/): one primary product chosen by ?item=, then small one-tap
// "add more with your design" tiles, a free-shipping nudge and "Add to cart · $total".
// Prices mirror the checkout Function: catalog + fee per item; the highest fee (to the nearest dollar) is
// charged once and every other item gets its own fee off. assets/product-form.js calls buildCartPayload().
if (!customElements.get('artlines-custom-hub')) {
  customElements.define(
    'artlines-custom-hub',
    class ArtlinesCustomHub extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.form = this.closest('form');
        this.el = {
          headline: this.querySelector('[data-al-hub-headline]'),
          price: this.querySelector('[data-al-hub-price]'),
          lede: this.querySelector('[data-al-hub-lede]'),
          primaryStep: this.querySelector('[data-al-hub-primary-step]'),
          primary: this.querySelector('[data-al-hub-primary]'),
          addonsWrap: this.querySelector('[data-al-hub-addons-wrap]'),
          addons: this.querySelector('[data-al-hub-addons]'),
          summary: this.querySelector('[data-al-hub-summary]'),
          error: this.querySelector('[data-al-hub-error]'),
          footnote: this.querySelector('[data-al-hub-footnote]'),
        };

        // Same rate Shopify uses for converted prices and passes to the checkout Function.
        this.rate = Number(window.Shopify?.currency?.rate) || 1;
        this.currency = window.Shopify?.currency?.active || 'USD';
        let data = { products: [], defaultFee: 15 };
        try {
          data = JSON.parse(this.querySelector('[data-al-hub-data]').textContent);
        } catch (e) {}
        this.products = (data.products || [])
          .map((product) => ({
            ...product,
            // Fees and pack prices are stored in USD; variant prices arrive already in the visitor's currency.
            baseFee: Number(product.fee),
            fee: this.convert(Number(product.fee)),
            garment: product.garment === true || product.garment === 'true',
            pack:
              product.pack && Number(product.pack.qty) >= 2
                ? { qty: Number(product.pack.qty), price: this.convert(Number(product.pack.price)) }
                : null,
          }))
          .sort((a, b) => Number(a.sort) - Number(b.sort));
        this.defaultFee = Number(data.defaultFee) || 15;
        this.rows = [];
        this.nextRowId = 1;
        this.primaryHandle = this.hubProduct()?.handle;
        this.garagePreset = false;

        this.applyUrlPresets();
        if (!this.rowsFor(this.primary()).length) this.addRow(this.primary());
        this.renderLede();
        this.render();
        this.placeStepOne();
      }

      // ---------- products and rows ----------

      hubProduct() {
        return this.products.find((product) => product.isHub) || this.products[0];
      }

      primary() {
        return this.products.find((product) => product.handle === this.primaryHandle) || this.hubProduct();
      }

      productByKey(key) {
        const wanted = String(key || '').toLowerCase();
        if (wanted === 'shirt' || wanted === 't-shirt' || wanted === 'tshirt') return this.hubProduct();
        return this.products.find(
          (product) => product.handle === wanted || product.handle === `custom-${wanted}` || product.label.toLowerCase() === wanted
        );
      }

      cheapestVariant(product) {
        const available = product.variants.filter((v) => v.available);
        return (available.length ? available : product.variants).reduce((a, b) => (b.price < a.price ? b : a));
      }

      addRow(product, variantId) {
        if (!product) return;
        const variant =
          product.variants.find((v) => v.id === variantId && v.available) ||
          (product.isHub && new URLSearchParams(window.location.search).has('variant') && this.mainVariant()) ||
          this.preferredVariant(product) ||
          product.variants.find((v) => v.available) ||
          product.variants[0];
        this.rows.push({ rowId: this.nextRowId++, handle: product.handle, variantId: variant.id });
      }

      // Default selection: Black (best seller, matches the ad creative) in L (custom buyers are mostly men 55+).
      // Falls back to Black in any size, then the first available variant.
      preferredVariant(product) {
        const preferences = { color: 'Black', colour: 'Black', size: 'L' };
        const wanted = product.options.map((name) => preferences[String(name).toLowerCase()] || null);
        const matches = (v, useAll) =>
          v.available &&
          wanted.every((value, i) => value === null || (!useAll && /^size$/i.test(product.options[i])) || v.options[i] === value);
        return product.variants.find((v) => matches(v, true)) || product.variants.find((v) => matches(v, false)) || null;
      }

      removeRow(rowId) {
        this.rows = this.rows.filter((row) => row.rowId !== rowId);
      }

      rowsFor(product) {
        return product ? this.rows.filter((row) => row.handle === product.handle) : [];
      }

      mainVariant() {
        const hub = this.products.find((product) => product.isHub);
        const id = Number(this.form?.querySelector('[name="id"]')?.value);
        return hub?.variants.find((v) => v.id === id && v.available);
      }

      // ?item=shirt|hoodie|mug|sticker|poster|garage-bundle (also ?bundle=garage), ?pair=1, ?car=<text>.
      // Missing or unknown ?item= falls back to the shirt.
      applyUrlPresets() {
        const params = new URLSearchParams(window.location.search);
        const item = (params.get('item') || '').toLowerCase();

        if (item === 'garage-bundle' || item === 'garage' || params.get('bundle') === 'garage') {
          this.garagePreset = true;
          this.primaryHandle = this.hubProduct()?.handle;
          ['shirt', 'mug', 'sticker'].forEach((key) => this.addRow(this.productByKey(key)));
        } else if (item) {
          const product = this.productByKey(item);
          if (product) this.primaryHandle = product.handle;
        }

        if (params.get('pair') === '1') {
          const hub = this.hubProduct();
          this.primaryHandle = hub.handle;
          while (this.rowsFor(hub).length < 2) this.addRow(hub);
        }

        const car = (params.get('car') || '').trim().slice(0, 120);
        if (car && this.form) {
          let attempts = 0;
          const fill = () => {
            const input = this.form.querySelector('input[name="properties[Car Model]"]');
            if (input) {
              if (!input.value) {
                input.value = car;
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
              return;
            }
            if (++attempts < 40) setTimeout(fill, 250);
          };
          fill();
        }
      }

      // ---------- pricing (mirror of the checkout Function) ----------

      pricingFor(rows) {
        const items = rows
          .map((row) => {
            const product = this.products.find((p) => p.handle === row.handle);
            const variant = product?.variants.find((v) => v.id === row.variantId);
            return { row, product, variant };
          })
          .filter((item) => item.product && item.variant)
          .map((item) => ({ ...item, full: item.variant.price / 100, fee: item.product.fee, baseFee: item.product.baseFee }));

        // Highest fee to the nearest dollar ($14.99 and $15 tie); ties go to the earliest item,
        // which is sent as the primary cart line.
        let bearer = null;
        items.forEach((item) => {
          if (!bearer || Math.round(item.baseFee) > Math.round(bearer.baseFee)) bearer = item;
        });
        items.forEach((item) => {
          item.isBearer = item === bearer;
          item.charged = items.length > 1 && !item.isBearer ? Math.max(0, item.full - item.fee) : item.full;
        });

        // Add-on packs (e.g. stickers 3 for $14.99), same rule as the checkout Function: every full pack of
        // add-on units (not the fee-bearing one) gets an extra discount so the pack totals the pack price.
        const packs = [];
        if (items.length > 1) {
          this.products.forEach((product) => {
            if (!product.pack) return;
            const addOns = items.filter((item) => item.product === product && !item.isBearer);
            const count = Math.floor(addOns.length / product.pack.qty);
            if (count < 1) return;
            const addOnPrice = addOns[0].charged;
            const extra = Math.round(count * (product.pack.qty * addOnPrice - product.pack.price) * 100) / 100;
            if (extra > 0) packs.push({ product, count, extra });
          });
        }
        const packSaving = packs.reduce((sum, pack) => sum + pack.extra, 0);
        const total = items.reduce((sum, item) => sum + item.charged, 0) - packSaving;
        const saved = items.reduce((sum, item) => sum + (item.full - item.charged), 0) + packSaving;
        const freeShipping = items.length >= 2 && items.some((item) => item.product.garment);
        return { items, bearer, total, saved, freeShipping, packs };
      }

      pricing() {
        return this.pricingFor(this.rows);
      }

      // Exact change to the order total if one more of `product` (cheapest variant) is added.
      addOnQuote(product) {
        const before = this.pricing();
        const variant = this.cheapestVariant(product);
        const after = this.pricingFor([...this.rows, { rowId: -1, handle: product.handle, variantId: variant.id }]);
        const delta = after.total - before.total;
        const standalone = variant.price / 100;
        return { delta, saving: Math.max(0, standalone - delta), freeShippingAfter: after.freeShipping };
      }

      convert(usd) {
        return Math.round(usd * this.rate * 100) / 100;
      }

      // Price in the visitor's currency, symbol only ("$64", "€46.50"); whole amounts drop ".00".
      money(amount) {
        const value = Math.round(amount * 100) / 100;
        try {
          const whole = Number.isInteger(value);
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: whole ? 0 : 2,
            maximumFractionDigits: 2,
          }).format(value);
        } catch (e) {
          return `$${value.toFixed(2).replace(/\.00$/, '')}`;
        }
      }

      // Same, plus the currency code outside the US ("$21.22 CAD") so a "$" amount isn't mistaken for USD.
      moneyWithCode(amount) {
        return this.currency === 'USD' ? this.money(amount) : `${this.money(amount)} ${this.currency}`;
      }

      // ---------- color swatches ----------

      isColorOption(name) {
        return /^colou?r$/i.test(String(name).trim());
      }

      // Approximate garment colors for swatches (Printful / Bella+Canvas names). Unknown names fall back to
      // the variant photo, then to a neutral chip with the name as tooltip.
      swatchColor(value) {
        const colors = {
          black: '#111111', white: '#ffffff', navy: '#1f2a44', red: '#c8102e', yellow: '#f6d85a',
          mauve: '#b88c8c', 'heather orange': '#e8895c', 'athletic heather': '#b7b7b4', 'true royal': '#2252a8',
          army: '#5b5f3f', maroon: '#6b2132', grey: '#8b8b8b', gray: '#8b8b8b', 'sport grey': '#a3a3a0',
          'dark heather': '#4a4a4a', 'forest green': '#2f4a35', charcoal: '#3d3d3d', pink: '#f2b6c6',
        };
        return colors[String(value).trim().toLowerCase()] || null;
      }

      // The photo most variants of this color use (a few variants point at the wrong photo).
      colorImage(product, optionIndex, value) {
        const counts = new Map();
        product.variants.forEach((v) => {
          if (v.options[optionIndex] === value && v.image) counts.set(v.image, (counts.get(v.image) || 0) + 1);
        });
        let best = null;
        counts.forEach((count, url) => {
          if (!best || count > best.count) best = { url, count };
        });
        return best?.url || null;
      }

      // ---------- rendering ----------

      renderLede() {
        if (!this.el.lede) return;
        const base = Math.round(this.defaultFee);
        const higher = this.products.filter((p) => Math.round(p.baseFee) > base);
        const top = Math.max(base, ...higher.map((p) => p.baseFee));
        // Higher fees (poster) are rare, so they live in a footnote instead of the headline copy.
        this.el.lede.textContent =
          `Your custom design is ${this.moneyWithCode(this.convert(base))}${higher.length ? '*' : ''}, paid once. ` +
          'Everything else you put it on is at our regular price.';
        if (this.el.footnote) {
          this.el.footnote.hidden = !higher.length;
          this.el.footnote.textContent = higher.length
            ? `* ${this.moneyWithCode(this.convert(top))} design fee when your order includes a ${higher.map((p) => p.label.toLowerCase()).join(' or ')}.`
            : '';
        }
      }

      render() {
        const pricing = this.pricing();
        const byRow = new Map(pricing.items.map((item) => [item.row.rowId, item]));
        const primary = this.primary();
        const primaryLabel = primary.label.toLowerCase();

        // Headline + one clear price
        const isGarage =
          this.garagePreset && ['shirt', 'mug', 'sticker'].every((key) => this.rowsFor(this.productByKey(key)).length);
        this.el.headline.textContent = isGarage ? 'Your car, on a shirt, mug and sticker' : `Your car, on a ${primaryLabel}`;
        this.el.price.innerHTML =
          pricing.items.length <= 1
            ? `${this.money(pricing.total || this.cheapestVariant(primary).price / 100)} <small>custom design included</small>`
            : `${this.money(pricing.total)} <small>for ${pricing.items.length} items, design included</small>`;

        // Primary product
        this.el.primaryStep.textContent = `Step 2 · Your ${primaryLabel}`;
        this.el.primary.innerHTML = '';
        const primaryRows = this.rowsFor(primary);
        const colorIndex = primary.options.findIndex((name) => this.isColorOption(name));
        primaryRows.forEach((row, index) => {
          const rowEl = this.renderRow(primary, row, byRow.get(row.rowId), {
            label: primaryRows.length > 1 ? `${primary.label} ${index + 1}` : '',
            removable: primaryRows.length > 1,
          });
          const variant = primary.variants.find((v) => v.id === row.variantId);
          const photo = colorIndex >= 0 && variant ? this.colorImage(primary, colorIndex, variant.options[colorIndex]) : null;
          if (photo) {
            const wrap = document.createElement('div');
            wrap.className = 'al-hub__primary-row';
            const img = document.createElement('img');
            img.className = 'al-hub__preview';
            img.src = photo;
            img.alt = `${primary.label} in ${variant.options[colorIndex]}`;
            img.width = 120;
            img.height = 120;
            wrap.append(img, rowEl);
            this.el.primary.appendChild(wrap);
          } else {
            this.el.primary.appendChild(rowEl);
          }
        });
        if (primaryRows.length) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'al-hub__cta2';
          const quote = this.addOnQuote(primary);
          more.textContent =
            primaryRows.length === 1
              ? `+ Add a 2nd ${primaryLabel} (same design): ${this.money(quote.delta)}${quote.freeShippingAfter && !pricing.freeShipping ? ' + free shipping' : ''}`
              : `+ Add another ${primaryLabel}: ${this.money(quote.delta)}`;
          more.addEventListener('click', () => {
            this.addRow(primary, primaryRows[primaryRows.length - 1].variantId);
            this.render();
          });
          this.el.primary.appendChild(more);
        } else {
          const add = document.createElement('button');
          add.type = 'button';
          add.className = 'al-hub__cta2';
          add.textContent = `+ Add a ${primaryLabel}`;
          add.addEventListener('click', () => {
            this.addRow(primary);
            this.render();
          });
          this.el.primary.appendChild(add);
        }

        // Add-on tiles
        this.el.addons.innerHTML = '';
        const others = this.products.filter((product) => product.handle !== primary.handle);
        this.el.addonsWrap.hidden = others.length === 0;
        others.forEach((product) => this.el.addons.appendChild(this.renderTile(product, byRow)));

        this.renderSummary(pricing);
        this.updateSubmitLabel(pricing);
      }

      renderTile(product, byRow) {
        const rows = this.rowsFor(product);
        const on = rows.length > 0;
        const tile = document.createElement('div');
        tile.className = `al-hub__tile${on ? ' al-hub__tile--on' : ''}`;

        const head = document.createElement('button');
        head.type = 'button';
        head.className = 'al-hub__tile-head';
        head.setAttribute('aria-pressed', String(on));
        let priceHtml;
        if (on) {
          const charged = rows.reduce((sum, row) => sum + (byRow.get(row.rowId)?.charged || 0), 0);
          priceHtml = `<span class="al-hub__tile-price">Added · ${this.money(charged)} <span class="al-hub__link">Remove</span></span>`;
        } else {
          const quote = this.addOnQuote(product);
          priceHtml =
            `<span class="al-hub__tile-price">+${this.money(quote.delta)}` +
            (quote.saving >= 1 ? `<span class="al-hub__badge">save ${this.money(quote.saving)}</span>` : '') +
            `</span>` +
            (product.pack ? `<span class="al-hub__pack-hint">or ${product.pack.qty} for ${this.money(product.pack.price)}</span>` : '');
        }
        head.innerHTML =
          (product.image ? `<img src="${product.image}" alt="" loading="lazy" width="40" height="40">` : '') +
          `<span><span class="al-hub__tile-name">${this.escape(product.label)}</span>${priceHtml}</span>`;
        head.addEventListener('click', () => {
          if (on) rows.forEach((row) => this.removeRow(row.rowId));
          else this.addRow(product);
          this.render();
        });
        tile.appendChild(head);

        rows.forEach((row) => {
          const hasOptions = product.options.some((name) => name !== 'Title');
          if (hasOptions || rows.length > 1) {
            tile.appendChild(this.renderRow(product, row, byRow.get(row.rowId), { removable: rows.length > 1, compact: true }));
          }
        });
        if (on) {
          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'al-hub__link';
          more.textContent = `+ another ${product.label.toLowerCase()}`;
          more.addEventListener('click', () => {
            this.addRow(product, rows[rows.length - 1].variantId);
            this.render();
          });
          tile.appendChild(more);
        }
        return tile;
      }

      renderRow(product, row, item, { label = '', removable = false, compact = false } = {}) {
        const wrap = document.createElement('div');
        wrap.className = 'al-hub__row';
        const variant = product.variants.find((v) => v.id === row.variantId) || product.variants[0];

        if (label) {
          const span = document.createElement('span');
          span.className = 'al-hub__row-label';
          span.textContent = label;
          wrap.appendChild(span);
        }

        const pick = (index, value) => {
          const wanted = [...variant.options];
          wanted[index] = value;
          const match =
            product.variants.find((v) => v.options.every((o, i) => o === wanted[i]) && v.available) ||
            product.variants.find((v) => v.options[index] === value && v.available);
          if (match) row.variantId = match.id;
          this.render();
        };

        product.options.forEach((name, index) => {
          if (name === 'Title') return;
          if (this.isColorOption(name)) {
            wrap.appendChild(this.renderSwatches(product, variant, index, name, compact, pick));
            return;
          }
          const select = document.createElement('select');
          select.setAttribute('aria-label', `${product.label} ${name}`);
          [...new Set(product.variants.map((v) => v.options[index]))].forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = compact ? value : `${name}: ${value}`;
            option.selected = variant.options[index] === value;
            select.appendChild(option);
          });
          select.addEventListener('change', () => pick(index, select.value));
          wrap.appendChild(select);
        });

        if (!compact && item) {
          const price = document.createElement('span');
          price.className = 'al-hub__row-price';
          price.innerHTML =
            item.charged < item.full ? `<s>${this.money(item.full)}</s> ${this.money(item.charged)}` : this.money(item.full);
          wrap.appendChild(price);
        }

        if (removable) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'al-hub__link';
          remove.textContent = 'Remove';
          remove.addEventListener('click', () => {
            this.removeRow(row.rowId);
            this.render();
          });
          wrap.appendChild(remove);
        }
        return wrap;
      }

      renderSwatches(product, variant, index, name, compact, pick) {
        const group = document.createElement('div');
        group.className = 'al-hub__option';
        const label = document.createElement('span');
        label.className = 'al-hub__option-label';
        label.textContent = `${name}: ${variant.options[index]}`;
        const swatches = document.createElement('div');
        swatches.className = `al-hub__swatches${compact ? ' al-hub__swatches--small' : ''}`;
        swatches.setAttribute('role', 'group');
        swatches.setAttribute('aria-label', `${product.label} ${name}`);

        [...new Set(product.variants.map((v) => v.options[index]))].forEach((value) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'al-hub__swatch';
          button.title = value;
          button.setAttribute('aria-label', value);
          button.setAttribute('aria-pressed', String(variant.options[index] === value));
          const color = this.swatchColor(value);
          const photo = color ? null : this.colorImage(product, index, value);
          if (color) button.style.backgroundColor = color;
          else if (photo) button.style.backgroundImage = `url("${photo}")`;
          const sameOtherOptions = product.variants.find(
            (v) => v.options[index] === value && v.options.every((o, i) => i === index || o === variant.options[i])
          );
          if (sameOtherOptions && !sameOtherOptions.available) button.dataset.unavailable = '';
          button.addEventListener('click', () => pick(index, value));
          swatches.appendChild(button);
        });

        group.append(label, swatches);
        return group;
      }

      renderSummary(pricing) {
        const { items, total, saved, freeShipping } = pricing;
        if (!items.length) {
          this.el.summary.innerHTML = '<p class="al-hub__line">Pick at least one item to put your design on.</p>';
          return;
        }

        let lines = '';
        if (items.length > 1) {
          lines =
            items
              .map((item) => {
                const variantTitle = item.variant.options.filter((o) => o !== 'Default Title').join(' / ');
                const note = item.isBearer ? 'includes your design' : 'same design';
                return `<div class="al-hub__line"><span>${this.escape(item.product.label)}${variantTitle ? ` · ${this.escape(variantTitle)}` : ''} <small>(${note})</small></span><span>${this.money(item.charged)}</span></div>`;
              })
              .join('') +
            (pricing.packs || [])
              .map(
                (pack) =>
                  `<div class="al-hub__line"><span>${this.escape(pack.product.label)} pack (${pack.product.pack.qty} for ${this.money(pack.product.pack.price)})</span><span>−${this.money(pack.extra)}</span></div>`
              )
              .join('') +
            `<div class="al-hub__line al-hub__line--total"><span>Total${saved > 0 ? ` · you save ${this.money(saved)}` : ''}</span><span>${this.money(total)}</span></div>`;
        }

        this.el.summary.innerHTML = lines;
        const nudge = document.createElement('p');
        nudge.className = `al-hub__nudge${freeShipping ? ' al-hub__nudge--free' : ''}`;
        if (freeShipping) {
          nudge.textContent = '✓ Free shipping on this order';
        } else {
          const best = this.cheapestFreeShippingAdd();
          if (best) {
            nudge.textContent = `Add a ${best.product.label.toLowerCase()} for +${this.money(best.delta)} and shipping is free.`;
            const add = document.createElement('button');
            add.type = 'button';
            add.className = 'al-hub__link';
            add.textContent = 'Add it';
            add.addEventListener('click', () => {
              this.addRow(best.product);
              this.render();
            });
            nudge.appendChild(add);
          } else {
            nudge.textContent = 'Orders with a shirt or hoodie plus one more item ship free.';
          }
        }
        this.el.summary.appendChild(nudge);
      }

      cheapestFreeShippingAdd() {
        let best = null;
        this.products.forEach((product) => {
          const quote = this.addOnQuote(product);
          if (quote.freeShippingAfter && (!best || quote.delta < best.delta)) best = { product, delta: quote.delta };
        });
        return best;
      }

      updateSubmitLabel(pricing) {
        const label = this.form?.querySelector('[name="add"] > span');
        if (!label || label.querySelector('*')) return;
        label.textContent = pricing.items.length ? `Add to cart · ${this.money(pricing.total)}` : window.variantStrings?.addToCart || 'Add to cart';
      }

      // Upload-Lift renders its fields (photo + Car Model) after load, at the end of the form. Move them
      // under the intro as Step 1 so the page reads 1 -> 2 -> 3. Moving keeps Upload-Lift's listeners.
      placeStepOne() {
        let attempts = 0;
        const place = () => {
          const target = this.form?.querySelector('.upload-container') || this.form?.querySelector('.cl-upload--wrapper');
          if (target) {
            if (!this.querySelector('.al-hub-step1')) {
              const label = document.createElement('p');
              label.className = 'al-hub-step1';
              label.textContent = 'Step 1 · Your car';
              this.querySelector('.al-hub__intro').after(label, target);
            }
            return;
          }
          if (++attempts < 40) setTimeout(place, 250);
        };
        place();
      }

      escape(text) {
        const div = document.createElement('div');
        div.textContent = String(text);
        return div.innerHTML;
      }

      // ---------- cart ----------

      isActive() {
        return true;
      }

      newDesignId() {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      }

      // Returns { items, sections, sections_url } for /cart/add, or null with an error shown.
      buildCartPayload(formData) {
        const { items, bearer } = this.pricing();
        if (!items.length) {
          this.showError('Pick at least one item to put your design on.');
          return null;
        }
        if (items.some((item) => !item.variant.available)) {
          this.showError('One of your choices is unavailable. Pick another size or color.');
          return null;
        }
        if (!this.validateEmail()) return null;
        this.showError('');
        this.subscribeIfOptedIn();

        const properties = {};
        let sections;
        let sectionsUrl;
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
          else if (key === 'sections') sections = value;
          else if (key === 'sections_url') sectionsUrl = value;
        }

        const designId = this.newDesignId();
        const ordered = [bearer, ...items.filter((item) => item !== bearer)];
        const payload = {
          items: ordered.map((item, index) => ({
            id: item.variant.id,
            quantity: 1,
            properties: {
              ...properties,
              ...(index > 0 ? { 'Custom design': 'Same design as your other items' } : {}),
              _al_design_id: designId,
              _al_bundle_role: index === 0 ? 'primary' : 'addon',
            },
          })),
        };
        if (sections) payload.sections = sections;
        if (sectionsUrl) payload.sections_url = sectionsUrl;
        return payload;
      }

      // Required: we email the drawing for approval. Shown next to the field so phone users see it.
      validateEmail() {
        const input = this.querySelector('[data-al-hub-email]');
        const error = this.querySelector('[data-al-hub-email-error]');
        if (!input) return true;
        const value = input.value.trim();
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
        input.setAttribute('aria-invalid', String(!ok));
        if (error) {
          error.hidden = ok;
          error.textContent = value ? 'Please check your email address.' : 'Please enter your email so we can send your drawing for approval.';
        }
        if (!ok) {
          this.errorMessage = error?.textContent || 'Please enter your email.';
          input.scrollIntoView({ block: 'center', behavior: 'smooth' });
          input.focus({ preventScroll: true });
        }
        return ok;
      }

      // Optional marketing consent: creates/updates a subscribed Shopify customer so follow-up emails can reach them.
      subscribeIfOptedIn() {
        const optin = this.querySelector('[data-al-hub-optin]');
        const email = this.querySelector('[data-al-hub-email]')?.value.trim();
        if (!optin?.checked || !email || this.subscribedEmail === email) return;
        this.subscribedEmail = email;
        const body = new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[email]': email,
          'contact[tags]': 'newsletter,custom-design-page',
        });
        fetch('/contact', { method: 'POST', body, headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).catch(() => {});
      }

      showError(message) {
        this.errorMessage = message;
        this.el.error.textContent = message;
        this.el.error.hidden = !message;
      }
    }
  );
}
